import { c as _c } from "react/compiler-runtime";
import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
interface UseDrawerDragOptions {
  /** Ref to the drawer DOM element — used for direct DOM height writes during drag. */
  drawerRef: React.RefObject<HTMLDivElement | null>;
  /** Mutable ref tracking the current drawer height (kept in sync outside of React state). */
  heightRef: React.MutableRefObject<number>;
  /** Minimum allowed height — drag below this applies rubber-banding. */
  minHeight: number;
  /** Maximum allowed height — drag above this applies rubber-banding. */
  maxHeight: number;
  /**
   * Called when the drag gesture ends, with the final height and computed swipe velocity
   * (positive = upward, negative = downward).
   */
  onDragEnd: (currentHeight: number, velocity: number) => void;
}

/**
 * Encapsulates the pointer-based drag gesture for a bottom drawer.
 *
 * During a drag, the drawer height is written directly to the DOM (bypassing React state)
 * for smooth 60 fps animation. When the gesture ends, `onDragEnd` is called so the consumer
 * can snap the drawer and sync React state.
 *
 * Returns touch and mouse event handlers to attach to the draggable area, plus an
 * `isDragging` flag for disabling CSS transitions while dragging.
 */
export const useDrawerDrag = t0 => {
  const $ = _c(36);
  const {
    drawerRef,
    heightRef,
    minHeight,
    maxHeight,
    onDragEnd
  } = t0;
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false);
  const wasDraggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);
  const startTimeRef = useRef(0);
  const lastYRef = useRef(0);
  const lastTimeRef = useRef(0);
  let t1;
  if ($[0] !== heightRef) {
    t1 = clientY => {
      const now = Date.now();
      setIsDragging(true);
      isDraggingRef.current = true;
      startYRef.current = clientY;
      startHeightRef.current = heightRef.current;
      startTimeRef.current = now;
      lastYRef.current = clientY;
      lastTimeRef.current = now;
    };
    $[0] = heightRef;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleStart = t1;
  let t2;
  if ($[2] !== drawerRef || $[3] !== heightRef || $[4] !== maxHeight || $[5] !== minHeight) {
    t2 = clientY_0 => {
      if (!isDraggingRef.current) {
        return;
      }
      const now_0 = Date.now();
      const deltaY = startYRef.current - clientY_0;
      let newHeight = startHeightRef.current + deltaY;
      if (newHeight > maxHeight) {
        newHeight = maxHeight;
      } else {
        if (newHeight < minHeight) {
          const overdrag = minHeight - newHeight;
          newHeight = minHeight - overdrag * 0.3;
        }
      }
      heightRef.current = newHeight;
      if (drawerRef.current) {
        drawerRef.current.style.height = `${newHeight}px`;
      }
      lastYRef.current = clientY_0;
      lastTimeRef.current = now_0;
    };
    $[2] = drawerRef;
    $[3] = heightRef;
    $[4] = maxHeight;
    $[5] = minHeight;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleMove = t2;
  let t3;
  if ($[7] !== heightRef || $[8] !== onDragEnd) {
    t3 = () => {
      if (!isDraggingRef.current) {
        return;
      }
      const timeDelta = lastTimeRef.current - startTimeRef.current;
      const distanceDelta = lastYRef.current - startYRef.current;
      const velocity = timeDelta > 0 ? -distanceDelta / timeDelta : 0;
      isDraggingRef.current = false;
      wasDraggingRef.current = Math.abs(distanceDelta) > 2;
      setIsDragging(false);
      onDragEnd(heightRef.current, velocity);
    };
    $[7] = heightRef;
    $[8] = onDragEnd;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handleEnd = t3;
  let t4;
  if ($[10] !== drawerRef) {
    t4 = target => {
      let el = target as HTMLElement | null;
      while (el && el !== drawerRef.current) {
        const style = window.getComputedStyle(el);
        const overflow = style.overflowY;
        if (overflow === "auto" || overflow === "scroll") {
          return true;
        }
        el = el.parentElement;
      }
      return false;
    };
    $[10] = drawerRef;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const isInsideScrollable = t4;
  const isInsideInteractive = _temp;
  const isInsideExplicitDragZone = _temp2;
  const scrollableRef = useRef(false);
  let t5;
  if ($[12] !== handleStart || $[13] !== isInsideScrollable) {
    t5 = e => {
      const isDragZone = isInsideExplicitDragZone(e.target);
      scrollableRef.current = !isDragZone && isInsideScrollable(e.target);
      if (scrollableRef.current) {
        return;
      }
      if (isInsideInteractive(e.target)) {
        return;
      }
      if (e.cancelable) {
        e.preventDefault();
      }
      if (e.touches[0]) {
        handleStart(e.touches[0].clientY);
      }
    };
    $[12] = handleStart;
    $[13] = isInsideScrollable;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleTouchStart = t5;
  let t6;
  if ($[15] !== handleMove) {
    t6 = e_0 => {
      if (scrollableRef.current) {
        return;
      }
      if (e_0.cancelable) {
        e_0.preventDefault();
      }
      if (e_0.touches[0]) {
        handleMove(e_0.touches[0].clientY);
      }
    };
    $[15] = handleMove;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const handleTouchMove = t6;
  let t7;
  if ($[17] !== handleEnd) {
    t7 = () => {
      handleEnd();
    };
    $[17] = handleEnd;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  const handleTouchEnd = t7;
  let t8;
  if ($[19] !== handleStart) {
    t8 = e_1 => {
      e_1.preventDefault();
      handleStart(e_1.clientY);
    };
    $[19] = handleStart;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const handleMouseDown = t8;
  let t9;
  if ($[21] !== handleMove) {
    t9 = e_2 => {
      handleMove(e_2.clientY);
    };
    $[21] = handleMove;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  const handleMouseMove = t9;
  let t10;
  if ($[23] !== handleEnd) {
    t10 = () => {
      handleEnd();
    };
    $[23] = handleEnd;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  const handleMouseUp = t10;
  let t11;
  let t12;
  if ($[25] !== handleMouseMove || $[26] !== handleMouseUp || $[27] !== isDragging) {
    t11 = () => {
      if (isDragging) {
        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "grabbing";
        document.body.style.userSelect = "none";
        return () => {
          document.removeEventListener("mousemove", handleMouseMove);
          document.removeEventListener("mouseup", handleMouseUp);
          document.body.style.cursor = "";
          document.body.style.userSelect = "";
        };
      }
    };
    t12 = [isDragging, handleMouseMove, handleMouseUp];
    $[25] = handleMouseMove;
    $[26] = handleMouseUp;
    $[27] = isDragging;
    $[28] = t11;
    $[29] = t12;
  } else {
    t11 = $[28];
    t12 = $[29];
  }
  useEffect(t11, t12);
  let t13;
  if ($[30] !== handleMouseDown || $[31] !== handleTouchEnd || $[32] !== handleTouchMove || $[33] !== handleTouchStart || $[34] !== isDragging) {
    t13 = {
      isDragging,
      wasDraggingRef,
      handleTouchStart,
      handleTouchMove,
      handleTouchEnd,
      handleMouseDown
    };
    $[30] = handleMouseDown;
    $[31] = handleTouchEnd;
    $[32] = handleTouchMove;
    $[33] = handleTouchStart;
    $[34] = isDragging;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  return t13;
};
function _temp(target_0) {
  const el_0 = target_0 as HTMLElement | null;
  return !!el_0?.closest?.("button, a, input, textarea, select, [role=\"button\"], [role=\"menuitem\"]");
}
function _temp2(target_1) {
  const el_1 = target_1 as HTMLElement | null;
  return !!el_1?.closest?.("[data-drawer-drag=\"true\"]");
}